import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Injector,
  Input,
  Output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractValueAccessor } from '../abstract-value-accessor';

export const BasicVariant = 'basic';
export const MultiselectVariant = 'multiselect';
export const PartiallyCheckedVariant = 'partially-checked';

export type CheckboxType =
  | typeof BasicVariant
  | typeof MultiselectVariant
  | typeof PartiallyCheckedVariant;

@Component({
  selector: 'app-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true
    },
  ],
})
export class CheckboxComponent extends AbstractValueAccessor {
  @HostBinding('class')
  private get classes(): string {
    return `${this.checkBoxVariantClass} ${this.checkboxType}`;
  }

  @HostBinding('class.checked')
  private get checked(): boolean {
    return this.value as boolean;
  }

  @HostBinding('class.disabled-checkbox')
  private get disabled(): boolean {
    return this.isDisabled;
  }

  @HostBinding('class.readonly-checkbox')
  @Input()
  isReadOnly = false;

  @Input()
  required: boolean;

  @Input()
  label: string;

  @Input()
  valueLabel: string;

  @Input()
  checkBoxVariant: CheckboxType = BasicVariant;

  @Input()
  checkboxType: 'primary' | 'secondary' | 'tertiary' = 'primary';

  get checkBoxVariantClass(): CheckboxType {
    if (this.partiallyChecked) {
      return PartiallyCheckedVariant;
    }
    return this.checkBoxVariant;
  }

  @Input()
  index: number;

  @Input()
  partiallyChecked: boolean;

  @Input()
  icon: string;

  @Input()
  doesHaveText = true;

  @Input()
  shouldChangeValueOnClick = true;

  @Output()
  readonly changeValue = new EventEmitter<{ checked: boolean }>();

  @Output()
  readonly changeManually = new EventEmitter<{ checked: boolean }>();

  constructor(injector: Injector, private cd: ChangeDetectorRef) {
    super(injector);
  }

  writeValue(value: boolean): void {
    super.writeValue(value);
    this.changeValue.emit({ checked: this.value });
    this.cd.markForCheck();
  }

  toggle(): void {
    if (!this.shouldChangeValueOnClick) {
      return;
    }

    if (this.isReadOnly) {
      return;
    }

    this.value = !this.value;
    this.changeValue.emit({ checked: this.value });
    this.changeManually.emit({ checked: this.value });
  }

  @HostListener('click')
  private handleHostClick(): void {
    if (!this.label) {
      this.toggle();
    }
  }
}
